import {
  Component,
  OnInit,
  HostListener,
  ElementRef,
  QueryList,
  ViewChildren,
  ChangeDetectorRef,
  Input,
  ChangeDetectionStrategy,
  Output,
  EventEmitter,
} from '@angular/core';
import formSchema from '../user-profile/form-json-schema/form-schema.json';
import {
  UserRegistration,
  Schema,
  UserProfileFormInterface,
  Validator,
} from './model/model.userProfileForm';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { stateOrCountryDropdownAnimation } from 'src/app/shared/reusable-animations/card-estimate-drawer-animations';
import { statesAllowedIso3Countries } from 'src/app/config/app-config';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  map,
  Observable,
  of,
  startWith,
} from 'rxjs';
import { StringUtils } from 'src/app/shared/utils/string-utils';
import { StatesConfig } from 'src/app/estimate/estimate.component';
import { StatesObj } from 'src/app/app.models';
import { config as countryConfig } from 'src/app/config/country-config';

@Component({
  selector: 'app-user-profile',
  templateUrl: './user-profile.component.html',
  styleUrls: ['./user-profile.component.scss'],
  animations: [stateOrCountryDropdownAnimation],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserProfileComponent implements OnInit {
  @Output() notifyUserRegistration: EventEmitter<UserRegistration> =
    new EventEmitter<UserRegistration>();
  private _countries?: any;
  @Input() public set countries(countries: any) {
    if (countries === null) {
      this._countries = this.convertCountryConfigToCountries(countryConfig);
    }
    else {
      this._countries = countries;
    }
  }

  public get countries(): any {
    return this._countries;
  }

  statesBehaviorSubject$: BehaviorSubject<StatesConfig[]> = new BehaviorSubject<
    StatesConfig[]
  >([]);
  filteredStates$: Observable<StatesConfig[]> | any =
    this.statesBehaviorSubject$.asObservable();
  filteredCountries$: Observable<any> | any;
  filteredCountryOfBirth$: Observable<any> | any;
  disableStateField: boolean = true;
  locationLookupInput = new FormControl('');
  userProfileSchema: UserProfileFormInterface =
    formSchema as UserProfileFormInterface;
  formGroup: FormGroup;
  activeDropdown: string | null = null;
  @ViewChildren('inputContainer') inputContainerElement: QueryList<ElementRef>;
  @HostListener('document:click', ['$event']) onClickOutside(
    event: MouseEvent
  ) {
    const targetElement = event.target as HTMLElement;
    if (this.inputContainerElement) {
      let clickedInside = false;

      this.inputContainerElement.forEach((container) => {
        if (container.nativeElement.contains(targetElement)) {
          clickedInside = true;
        }
      });
      if (!clickedInside) {
        this.activeDropdown = null;
      }
    }
  }
  constructor(private fb: FormBuilder, private cdr: ChangeDetectorRef) {}
  ngOnInit(): void {
    this.formGroup = this.buildForm(this.userProfileSchema.schemas);
    // TODO: integrate google location lookup
    this.locationLookupInput.valueChanges.subscribe(
      (value: string | null | undefined) => {
        console.log('Location lookup', value);
      }
    );

    if (this.disableStateField) {
      this.formGroup.get('state')?.disable();
    } else {
      this.formGroup.get('state')?.enable();
    }
    this.filteredCountryOfBirth$ = this.formGroup
      .get('countryOfBirth')
      ?.valueChanges.pipe(
        startWith(''),
        map((val) => this.countryOfBirthFilter(val || ''))
      );

    this.filteredCountries$ = this.formGroup?.get('country')?.valueChanges.pipe(
      startWith(''),
      map((value) => this.countriesFilter(value || ''))
    );
    this.filteredStates$ = this.formGroup?.get('state')?.valueChanges.pipe(
      startWith(''),
      map((value) => {
        return this.statesFilter(value || '');
      })
    );
    this.onCountryFilter();
    this.onStateFilter();
    this.onCountryOfBirthFilter();
  }
  private statesFilter(value: string): StatesConfig[] {
    let states = this.statesBehaviorSubject$.getValue();
    const filterValue =
      StringUtils.removeDiacriticsAndConvertToLowercase(value);
    return states.filter((option: StatesObj) => {
      return (
        (option?.name &&
          StringUtils.removeDiacriticsAndConvertToLowercase(
            option?.name
          ).startsWith(filterValue)) ||
        (option?.code &&
          StringUtils.removeDiacriticsAndConvertToLowercase(
            option?.code
          ).startsWith(filterValue))
      );
    });
  }

  private resetStateController() {
    this.formGroup.get('state')?.setValue('');
    this.formGroup.get('state')?.disable();
    this.statesBehaviorSubject$.next([]);
  }

  private countriesFilter(value: any): any {
    const countryFilterValue =
      StringUtils.removeDiacriticsAndConvertToLowercase(value);
    return this.countries.filter((country: any) => {
      return (
        (country.name &&
          StringUtils.removeDiacriticsAndConvertToLowercase(
            country.name
          ).startsWith(countryFilterValue)) ||
        (country.code &&
          StringUtils.removeDiacriticsAndConvertToLowercase(
            country.code
          ).startsWith(countryFilterValue))
      );
    });
  }
  private countryOfBirthFilter(value: any): any {
    const countryFilterValue =
      StringUtils.removeDiacriticsAndConvertToLowercase(value);
    return this.countries.filter((country: any) => {
      return (
        (country.name &&
          StringUtils.removeDiacriticsAndConvertToLowercase(
            country.name
          ).startsWith(countryFilterValue)) ||
        (country.code &&
          StringUtils.removeDiacriticsAndConvertToLowercase(
            country.code
          ).startsWith(countryFilterValue))
      );
    });
  }
  private buildForm(schemas: Schema[]): FormGroup {
    const group: { [key: string]: any } = {};
    schemas.forEach((field) => {
      if (field.type === 'object' && field.properties) {
        group[field.name] = this.buildForm(Object.values(field.properties));
      } else {
        group[field.name] = new FormControl(field.value || '', {
          validators: this.mapValidators(field.validators),
          updateOn: 'change',
        });
      }
    });
    return this.fb.group(group);
  }

  private mapValidators(validatorsObj: Validator | undefined): any[] {
    const validator: any[] = [];
    if (!validatorsObj) return validator;
    if (validatorsObj.required) {
      validator.push(Validators.required);
    }
    if (typeof validatorsObj.minLength === 'number') {
      validator.push(Validators.minLength(validatorsObj.minLength));
    }
    if (typeof validatorsObj.maxLength === 'number') {
      validator.push(Validators.maxLength(validatorsObj.maxLength));
    }
    if (validatorsObj.pattern) {
      validator.push(Validators.pattern(validatorsObj.pattern));
    }
    return validator;
  }

  objectToArray(obj: { [key: string]: Schema } | undefined): Schema[] {
    return Object.keys(obj!).map((key) => obj![key]);
  }

  getAsteriskClass(fieldName: string): boolean {
    const control = this.formGroup.get(fieldName);
    if (!control) return false;

    const monthControl = control.get('month');
    const dayControl = control.get('day');
    const yearControl = control.get('year');
    const isInvalidAndTouchedOrDirty = (ctrl: any) =>
      ctrl && ctrl.invalid && (ctrl.touched || ctrl.dirty);

    const monthHasRequiredError =
      monthControl?.errors?.required &&
      isInvalidAndTouchedOrDirty(monthControl);
    const dayHasRequiredError =
      dayControl?.errors?.required && isInvalidAndTouchedOrDirty(dayControl);
    const yearHasRequiredError =
      yearControl?.errors?.required && isInvalidAndTouchedOrDirty(yearControl);

    if (monthHasRequiredError || dayHasRequiredError || yearHasRequiredError) {
      return true;
    }
    return false;
  }

  onFieldClick(fieldName: string, event: MouseEvent) {
    event.stopPropagation();
    this.activeDropdown = this.activeDropdown === fieldName ? null : fieldName;
  }
  setActiveDropdown(fieldName: string) {
    this.activeDropdown = this.activeDropdown === fieldName ? null : fieldName;
  }

  private updatedFormObject(formValue: UserRegistration): UserRegistration {
    const updatedForm = { ...formValue };
    const countries = [...this.countries];

    const country = countries.find(
      (c) =>
        StringUtils.removeDiacriticsAndConvertToLowercase(c.name) ===
        StringUtils.removeDiacriticsAndConvertToLowercase(formValue?.country)
    );

    if (country) {
      updatedForm.country = country.code || ''; // TODO: Tim: had to add an || '' when implementing the countries from API call. Need to double check that this isn't breaking the form

      const state = country.states?.find(
        (state: any) => // Todo: fix types
          StringUtils.removeDiacriticsAndConvertToLowercase(state.name) ===
          StringUtils.removeDiacriticsAndConvertToLowercase(formValue.state)
      );

      if (state) {
        updatedForm.state = state.code;
      }
    }

    const countryOfBirth = countries.find(
      (c) =>
        StringUtils.removeDiacriticsAndConvertToLowercase(c.name) ===
        StringUtils.removeDiacriticsAndConvertToLowercase(
          formValue.countryOfBirth
        )
    );

    if (countryOfBirth) {
      updatedForm.countryOfBirth = countryOfBirth.code || ''; // TODO: Tim: had to add an || '' when implementing the countries from API call. Need to double check that this isn't breaking the form
    }

    return updatedForm;
  }

  convertCountryConfigToCountries(countriesConfig: any): any[] {
    let countries: any[] = [];
    
    countriesConfig.forEach((country: any) => {
      let countryObj: any = {
        iso2code: country.iso2Code,
        name: country.name,
        code: country.code,
      }
      if (!!country.states[0]) {
        countryObj['states'] = country.states;
      }
      
      countries.push(countryObj);
    })

    return countries;
  }

  onSubmit(formValue: UserRegistration) {
    if (this.formGroup.valid) {
      this.notifyUserRegistration.emit(this.updatedFormObject(formValue));
    } else {
      Object.keys(this.formGroup.controls).forEach((field) => {
        const control = this.formGroup.get(field);
        control?.markAsTouched({ onlySelf: true });
      });
    }
  }

  onSelectdCountryOfBirth(country: any, fieldName: string) {
    this.formGroup.get(fieldName)?.setValue(country.name, { emitEvent: false });
    this.activeDropdown = null;
  }

  onSelectedCountryOption(country: any, fieldName: string) {
    if (!statesAllowedIso3Countries.includes(country.code)) {
      this.resetStateController();
    } else {
      this.formGroup.get('state')?.enable();
      this.statesBehaviorSubject$.next(country.states || []);
    }
    this.formGroup.get(fieldName)?.setValue(country.name, { emitEvent: false });
    this.activeDropdown = null;
  }

  onSelectedState(state: StatesObj, fieldName: string) {
    this.formGroup.get(fieldName)?.setValue(state.name, { emitEvent: false });
    this.activeDropdown = null;
  }

  private onCountryOfBirthFilter() {
    const countryOfBirthControl = this.formGroup.get('countryOfBirth');
    const countryOfBirthValueChanges$ =
      countryOfBirthControl?.valueChanges || of(null);
    combineLatest<[any, string | null]>([
      this.filteredCountryOfBirth$,
      countryOfBirthValueChanges$,
    ])
      .pipe(debounceTime(0), distinctUntilChanged())
      .subscribe(([country, value]) => {
        const filterValue = StringUtils.removeDiacriticsAndConvertToLowercase(
          value!
        );
        if (filterValue && !country.length) {
          this.formGroup
            .get('countryOfBirth')
            ?.setErrors({ invalidOption: true });
        } else if (!filterValue && country.length) {
          this.formGroup.get('countryOfBirth')?.setErrors({ required: true });
        } else if (
          filterValue ===
          StringUtils.removeDiacriticsAndConvertToLowercase(country[0]?.name)
        ) {
          const matchedOption = country.filter(
            (country: any) =>
              StringUtils.removeDiacriticsAndConvertToLowercase(
                country?.name
              ) === filterValue
          );
          if (matchedOption.length > 1) {
            const selectedOption =
              matchedOption.find(
                (country: any) =>
                  StringUtils.removeDiacriticsAndConvertToLowercase(
                    country?.name
                  ) === 'united states of america'
              ) || matchedOption[0];
            this.formGroup
              .get('countryOfBirth')
              ?.setValue(selectedOption?.name, {
                emitEvent: false,
              });
            this.formGroup.get('countryOfBirth')?.setErrors(null);
            setTimeout(() => {
              this.activeDropdown = null;
            }, 100);
          } else if (matchedOption.length === 1) {
            this.formGroup
              .get('countryOfBirth')
              ?.setValue(matchedOption[0]?.name, {
                emitEvent: false,
              });
            this.formGroup.get('countryOfBirth')?.setErrors(null);
            this.activeDropdown = null;
          }
          this.formGroup.get('countryOfBirth')?.setErrors(null);
        } else {
          this.formGroup
            .get('countryOfBirth')
            ?.setErrors({ invalidOption: true });
        }
        this.cdr.detectChanges();
      });
  }
  private onCountryFilter() {
    const countryControl = this.formGroup?.get('country');
    const countryValueChanges$ = countryControl?.valueChanges || of(null);
    combineLatest<[any, string | null]>([
      this.filteredCountries$,
      countryValueChanges$,
    ])
      .pipe(debounceTime(0), distinctUntilChanged())
      .subscribe(([country, value]) => {
        const filterValue = StringUtils.removeDiacriticsAndConvertToLowercase(
          value!
        );
        if (filterValue && !country.length) {
          this.formGroup.get('country')?.setErrors({ invalidOption: true });
          this.activeDropdown = null;
          this.resetStateController();
        } else if (!filterValue && country.length) {
          this.formGroup.get('country')?.setErrors({ required: true });
          this.resetStateController();
        } else if (
          filterValue ===
          StringUtils.removeDiacriticsAndConvertToLowercase(country[0]?.name)
        ) {
          const matchedOption = country.filter(
            (country: any) =>
              StringUtils.removeDiacriticsAndConvertToLowercase(
                country?.name
              ) === filterValue
          );
          if (matchedOption.length > 1) {
            const selectedOption =
              matchedOption.find(
                (country: any) =>
                  StringUtils.removeDiacriticsAndConvertToLowercase(
                    country?.name
                  ) === 'united states of america'
              ) || matchedOption[0];
            this.formGroup.get('country')?.setValue(selectedOption?.name, {
              emitEvent: false,
            });
            if (!statesAllowedIso3Countries.includes(selectedOption.code)) {
              this.resetStateController();
            } else {
              this.formGroup.get('state')?.enable();
              this.statesBehaviorSubject$.next(selectedOption.states || []);
              this.disableStateField = false;
            }

            this.formGroup.get('country')?.setErrors(null);
            this.activeDropdown = null;
          } else if (matchedOption.length === 1) {
            this.formGroup.get('country')?.setValue(matchedOption[0]?.name, {
              emitEvent: false,
            });
            if (!statesAllowedIso3Countries.includes(matchedOption[0].code)) {
              this.resetStateController();
            } else {
              this.formGroup.get('state')?.enable();
              this.disableStateField = false;
              this.statesBehaviorSubject$.next(matchedOption[0].states || []);
            }
            this.formGroup.get('country')?.setErrors(null);
            this.activeDropdown = null;
          }
          this.formGroup.get('country')?.setErrors(null);
        } else {
          this.formGroup.get('country')?.setErrors({ invalidOption: true });
          this.resetStateController();
        }
        this.cdr.detectChanges();
      });
  }

  private onStateFilter() {
    const stateControl = this.formGroup?.get('state');
    const stateValueChange$ = stateControl?.valueChanges;
    combineLatest<[StatesConfig[], string | null]>([
      this.filteredStates$,
      stateValueChange$!,
    ])
      .pipe(debounceTime(0), distinctUntilChanged())
      .subscribe(([state, value]) => {
        const filterValue = StringUtils.removeDiacriticsAndConvertToLowercase(
          value!
        );
        if (state.length === 1) {
          const option = state[0];
          const isMatch =
            filterValue ===
              StringUtils.removeDiacriticsAndConvertToLowercase(option?.name) ||
            filterValue ===
              StringUtils.removeDiacriticsAndConvertToLowercase(option?.code);
          if (isMatch) {
            this.formGroup
              .get('state')
              ?.setValue(option?.name, { emitEvent: false });
            this.activeDropdown = null;
          }
        } else if (state.length > 1) {
          const matchingStates = state.filter(
            (option) =>
              filterValue ===
                StringUtils.removeDiacriticsAndConvertToLowercase(
                  option?.name
                ) ||
              filterValue ===
                StringUtils.removeDiacriticsAndConvertToLowercase(option?.code)
          );
          if (matchingStates.length === 1) {
            const option = matchingStates[0];
            this.formGroup
              .get('state')
              ?.setValue(option?.name, { emitEvent: false });
            this.activeDropdown = null;
          }
        } else if (filterValue && !state.length) {
          this.formGroup.get('state')?.setErrors({ invalidOption: true });
        } else if (!filterValue && state.length) {
          this.formGroup.get('state')?.setErrors({ required: true });
        } else {
          this.formGroup.get('state')?.setErrors({ invalidOption: true });
        }
        this.cdr.detectChanges();
      });
  }
}
